'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';

/**
 * TenantContext - CR-005 Multi-Tenant State Management
 *
 * Provides tenant context throughout the application including:
 * - Current active tenant
 * - List of available tenants for the user
 * - Tenant switching functionality
 * - Token-based tenant detection
 */

export interface TenantInfo {
  tenant_id: string;
  tenant_name: string;
  role: string;
  is_default: boolean;
  allowedModules?: string[];
  permissions?: string[];
}

export interface JWTPayload {
  sub: string;
  email: string;
  tenant_id: string;
  tenant_name?: string;
  user_type: string;
  roles?: string[];
  permissions?: string[];
  tenants?: TenantInfo[];
  default_tenant?: boolean;
  exp: number;
}

interface TenantContextValue {
  currentTenant: TenantInfo | null;
  availableTenants: TenantInfo[];
  switchTenant: (tenantId: string) => Promise<void>;
  refreshTenantContext: () => void;
  loading: boolean;
  error: string | null;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

interface TenantProviderProps {
  children: React.ReactNode;
}

export function TenantProvider({ children }: TenantProviderProps) {
  const [currentTenant, setCurrentTenant] = useState<TenantInfo | null>(null);
  const [availableTenants, setAvailableTenants] = useState<TenantInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Initialize tenant context from JWT token
   * Extracts current tenant and available tenants from access token
   */
  const initializeTenantContext = useCallback(() => {
    try {
      setLoading(true);
      setError(null);

      // Get access token from localStorage
      const accessToken = localStorage.getItem('access_token');

      if (!accessToken || accessToken === '') {
        setCurrentTenant(null);
        setAvailableTenants([]);
        setLoading(false);
        return;
      }

      // Decode JWT token
      const decoded = jwtDecode<JWTPayload>(accessToken);

      // Check if token is expired
      if (decoded.exp && decoded.exp < Date.now() / 1000) {
        console.warn('Token expired, clearing tenant context');
        setCurrentTenant(null);
        setAvailableTenants([]);
        setLoading(false);
        return;
      }

      // Extract current tenant from token
      if (decoded.tenant_id) {
        const current: TenantInfo = {
          tenant_id: decoded.tenant_id,
          tenant_name: decoded.tenant_name || 'Unknown Tenant',
          role: decoded.user_type || 'unknown',
          is_default: decoded.default_tenant || false,
          permissions: decoded.permissions || []
        };
        setCurrentTenant(current);
      }

      // Extract available tenants from token
      if (decoded.tenants && Array.isArray(decoded.tenants)) {
        setAvailableTenants(decoded.tenants);
      } else {
        // If no tenants array, use current tenant as the only available tenant
        if (decoded.tenant_id) {
          setAvailableTenants([{
            tenant_id: decoded.tenant_id,
            tenant_name: decoded.tenant_name || 'Unknown Tenant',
            role: decoded.user_type || 'unknown',
            is_default: decoded.default_tenant || false
          }]);
        }
      }

      setLoading(false);
    } catch (err) {
      console.error('Error initializing tenant context:', err);
      setError(err instanceof Error ? (err instanceof Error ? err.message : String(err)) : 'Failed to initialize tenant context');
      setCurrentTenant(null);
      setAvailableTenants([]);
      setLoading(false);
    }
  }, []);

  /**
   * Switch to a different tenant
   * Calls the backend switch-tenant API and updates tokens
   */
  const switchTenant = useCallback(async (tenantId: string) => {
    try {
      setLoading(true);
      setError(null);

      const accessToken = localStorage.getItem('access_token');

      if (!accessToken) {
        throw new Error('No access token found');
      }

      // Call switch-tenant API
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
      const response = await fetch(`${apiUrl}/api/v1/auth/switch-tenant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ tenant_id: tenantId })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to switch tenant' }));
        throw new Error(errorData.detail || 'Failed to switch tenant');
      }

      const data = await response.json();

      // Store new tokens
      localStorage.setItem('access_token', data.access_token);
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token);
      }

      // Update cookies for middleware
      document.cookie = `token=${data.access_token}; path=/; max-age=${data.expires_in}; SameSite=Lax`;
      if (data.refresh_token) {
        document.cookie = `refresh_token=${data.refresh_token}; path=/; max-age=604800; SameSite=Lax`;
      }

      // Update current tenant
      if (data.current_tenant) {
        setCurrentTenant(data.current_tenant);
      }

      // Refresh the page to reload all data with new tenant context
      window.location.reload();
    } catch (err) {
      console.error('Error switching tenant:', err);
      setError(err instanceof Error ? (err instanceof Error ? err.message : String(err)) : 'Failed to switch tenant');
      setLoading(false);
    }
  }, []);

  /**
   * Refresh tenant context (useful after login or token refresh)
   */
  const refreshTenantContext = useCallback(() => {
    initializeTenantContext();
  }, [initializeTenantContext]);

  // Initialize on mount
  useEffect(() => {
    initializeTenantContext();
  }, [initializeTenantContext]);

  const value: TenantContextValue = {
    currentTenant,
    availableTenants,
    switchTenant,
    refreshTenantContext,
    loading,
    error
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

/**
 * Hook to access tenant context
 * @throws Error if used outside TenantProvider
 */
export function useTenant() {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}
